import React, { useCallback, useEffect, useRef, useState } from "react";
import { Popup } from "devextreme-react/popup";
import Button from "devextreme-react/button";
import notify from "devextreme/ui/notify";
import { QRCode } from "antd";
import { Envase } from "../../dtos/Envase";
import { ConfiguracionDto } from "../../dtos/Configuracion";
import { apiService } from "../../services/apiService";
import infraLogo from "../../assets/img/infra-logo-mobile.png";
import "./ImprimirEtiquetaPopup.css";

/**
 * Geometría idéntica a `LabelGeometry` / ZPL de la app móvil (Honeywell/Zebra,
 * 203 dpi, etiqueta física de 2" x 1"). Mantener estos valores sincronizados
 * con lib/components/label_geometry.dart del repo etiquetado-envases-mobile-app.
 */
export const LABEL_WIDTH_MM = 50.8;
export const LABEL_HEIGHT_MM = 25.4;

/**
 * El ZPL móvil redimensiona el logo a 184x60 dots @203dpi SIN mantener
 * proporción (estira la imagen), por lo que se ve achatado/ensanchado.
 * Se replica el mismo estiramiento aquí (object-fit: fill) para que ambas
 * etiquetas luzcan igual, aunque el logo original no tenga esa proporción.
 */
const LOGO_X_MM = 4.0;
const LOGO_Y_MM = 3.0;
const LOGO_WIDTH_MM = 23; // 184 dots @203dpi
const LOGO_HEIGHT_MM = 7.5; // 60 dots @203dpi

const TITLE_X_MM = 3.0;
const TITLE_Y_MM = 10.5;
const TITLE_FONT_MM = 3.0; // 24 dots @203dpi

const SERIE_X_MM = 3.0;
const SERIE_Y_MM = 16.5;
const SERIE_FONT_MM = 2.75; // 22 dots @203dpi
const SERIE_LINE_SPACING_MM = 3.8;
const SERIE_BREAK_INDEX = 15;

const QR_SIZE_MM = 19.0;
const QR_RIGHT_MARGIN_MM = 1.0;
/** Fijo en mm (no % / transform) para que la posición no varíe entre renders de impresión. */
const QR_TOP_MM = (LABEL_HEIGHT_MM - QR_SIZE_MM) / 2;

type ImprimirEtiquetaPopupProps = {
  visible: boolean;
  onClose: () => void;
  activo: Envase | null;
  titulo?: string;
};

const buildGarantiaUrl = (baseUrl: string, numeroSerie: string) => {
  const base = baseUrl.trim();
  if (!base) return "";
  return base.endsWith("/") ? `${base}${numeroSerie}` : `${base}/${numeroSerie}`;
};

const buildQrContent = (numeroSerie: string, garantiaUrl: string) =>
  `${numeroSerie}\nUrl: ${garantiaUrl}`;

const splitSerie = (numeroSerie: string) => {
  if (numeroSerie.length <= SERIE_BREAK_INDEX) {
    return { line1: numeroSerie, line2: "" };
  }
  return {
    line1: numeroSerie.substring(0, SERIE_BREAK_INDEX),
    line2: numeroSerie.substring(SERIE_BREAK_INDEX),
  };
};

const PRINT_STYLES = `
  @page { size: ${LABEL_WIDTH_MM}mm ${LABEL_HEIGHT_MM}mm; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body {
    width: ${LABEL_WIDTH_MM}mm;
    height: ${LABEL_HEIGHT_MM}mm;
    font-family: "Arial Narrow", Arial, Helvetica, sans-serif;
    background: #fff;
    color: #000;
  }
  body { display: flex; align-items: stretch; justify-content: stretch; }
  .etiqueta-mobile {
    position: relative;
    width: ${LABEL_WIDTH_MM}mm;
    height: ${LABEL_HEIGHT_MM}mm;
    background: #fff;
    color: #000;
  }
  .etiqueta-mobile-logo {
    position: absolute;
    left: ${LOGO_X_MM}mm;
    top: ${LOGO_Y_MM}mm;
    width: ${LOGO_WIDTH_MM}mm;
    height: ${LOGO_HEIGHT_MM}mm;
    object-fit: fill;
    filter: grayscale(1);
    image-rendering: crisp-edges;
  }
  .etiqueta-mobile-title {
    position: absolute;
    left: ${TITLE_X_MM}mm;
    top: ${TITLE_Y_MM}mm;
    font-size: ${TITLE_FONT_MM}mm;
    font-weight: 700;
    line-height: 1;
    color: #000;
    white-space: nowrap;
  }
  .etiqueta-mobile-serie {
    position: absolute;
    left: ${SERIE_X_MM}mm;
    top: ${SERIE_Y_MM}mm;
    font-size: ${SERIE_FONT_MM}mm;
    font-weight: 700;
    letter-spacing: 0.02em;
    line-height: ${SERIE_LINE_SPACING_MM}mm;
    color: #000;
    word-break: break-all;
  }
  .etiqueta-mobile-qr {
    position: absolute;
    right: ${QR_RIGHT_MARGIN_MM}mm;
    top: ${QR_TOP_MM}mm;
    width: ${QR_SIZE_MM}mm;
    height: ${QR_SIZE_MM}mm;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .etiqueta-mobile-qr canvas,
  .etiqueta-mobile-qr img,
  .etiqueta-mobile-qr svg {
    width: ${QR_SIZE_MM}mm !important;
    height: ${QR_SIZE_MM}mm !important;
    max-width: ${QR_SIZE_MM}mm !important;
    max-height: ${QR_SIZE_MM}mm !important;
  }
`;

const ImprimirEtiquetaPopup: React.FC<ImprimirEtiquetaPopupProps> = ({
  visible,
  onClose,
  activo,
  titulo = "Reimprimir etiqueta",
}) => {
  const labelRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [garantiaUrl, setGarantiaUrl] = useState("");

  useEffect(() => {
    if (visible) document.body.classList.add("no-scroll");
    else document.body.classList.remove("no-scroll");
    return () => document.body.classList.remove("no-scroll");
  }, [visible]);

  useEffect(() => {
    if (!visible || !activo?.numeroSerie) {
      setGarantiaUrl("");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const cfg = await apiService.get<ConfiguracionDto>("/api/Configuracion");
        if (cancelled) return;
        setGarantiaUrl(buildGarantiaUrl(cfg?.baseURL ?? "", activo.numeroSerie));
      } catch {
        if (!cancelled) setGarantiaUrl("");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, activo?.numeroSerie]);

  useEffect(() => {
    return () => {
      if (iframeRef.current?.parentNode) {
        iframeRef.current.parentNode.removeChild(iframeRef.current);
      }
      iframeRef.current = null;
    };
  }, []);

  const ensurePrintIframe = useCallback(() => {
    if (iframeRef.current?.contentWindow) return iframeRef.current;

    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.title = "Impresión de etiqueta";
    iframe.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
    document.body.appendChild(iframe);
    iframeRef.current = iframe;
    return iframe;
  }, []);

  const waitForImages = useCallback((doc: Document) => {
    const images = Array.from(doc.images);
    const pending = images.filter((img) => !img.complete);
    if (pending.length === 0) return Promise.resolve();

    return Promise.all(
      pending.map(
        (img) =>
          new Promise<void>((resolve) => {
            const done = () => resolve();
            img.addEventListener("load", done, { once: true });
            img.addEventListener("error", done, { once: true });
            // Salvaguarda: no bloquear la impresión indefinidamente si una
            // imagen nunca dispara load/error (evita el hang de la vista
            // previa en Chromium/Brave al imprimir a un driver ZPL "raw").
            setTimeout(done, 1500);
          })
      )
    ).then(() => undefined);
  }, []);

  const handlePrint = useCallback(() => {
    if (!activo?.numeroSerie || !labelRef.current) return;
    if (activo.statusActivo === "Inactivo") {
      notify("No se puede imprimir la etiqueta porque el envase está inactivo", "error", 3000);
      return;
    }

    const iframe = ensurePrintIframe();
    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!doc || !win) return;

    doc.open();
    doc.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Etiqueta ${activo.numeroSerie}</title>
  <style>${PRINT_STYLES}</style>
</head>
<body>${labelRef.current.outerHTML}</body>
</html>`);
    doc.close();

    waitForImages(doc).then(() => {
      // Un frame extra asegura que el layout/paint ya se aplicó antes de
      // disparar el diálogo de impresión.
      win.requestAnimationFrame(() => {
        win.requestAnimationFrame(() => {
          win.focus();
          win.print();
        });
      });
    });
  }, [activo, ensurePrintIframe, waitForImages]);

  if (!visible || !activo) return null;

  const numeroSerie = activo.numeroSerie || "—";
  const { line1, line2 } = splitSerie(numeroSerie);
  const qrContent = buildQrContent(numeroSerie, garantiaUrl);

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      dragEnabled={false}
      position="center"
      shading
      closeOnOutsideClick
      showCloseButton
      showTitle={false}
      width="92vw"
      maxWidth={560}
      height="auto"
      maxHeight="90vh"
      wrapperAttr={{ class: "imprimir-etiqueta-popup-wrapper" }}
    >
      <div className="imprimir-etiqueta-popup">
        <header className="imprimir-etiqueta-header">
          <div>
            <h2 className="imprimir-etiqueta-title">{titulo}</h2>
            <p className="imprimir-etiqueta-subtitle">
              Verifica la información antes de enviar la etiqueta a impresión
            </p>
          </div>
        </header>

        <div className="imprimir-etiqueta-body">
          <div className="imprimir-etiqueta-preview-frame">
            <div
              className="etiqueta-mobile"
              ref={labelRef}
              style={{
                width: `${LABEL_WIDTH_MM}mm`,
                height: `${LABEL_HEIGHT_MM}mm`,
              }}
            >
              <img
                className="etiqueta-mobile-logo"
                src={infraLogo}
                alt="INFRA"
              />
              <div className="etiqueta-mobile-title">Control Activos</div>
              <div className="etiqueta-mobile-serie">
                <div>{line1}</div>
                {line2 ? <div>{line2}</div> : null}
              </div>
              <div className="etiqueta-mobile-qr">
                <QRCode
                  type="svg"
                  value={qrContent}
                  size={72}
                  errorLevel="M"
                  bordered={false}
                  color="#000"
                  bgColor="#fff"
                />
              </div>
            </div>
          </div>
        </div>

        <footer className="imprimir-etiqueta-footer">
          <Button text="Cancelar" stylingMode="outlined" onClick={onClose} />
          <Button
            text="Imprimir"
            type="default"
            icon="print"
            stylingMode="contained"
            disabled={activo.statusActivo === "Inactivo"}
            onClick={handlePrint}
          />
        </footer>
      </div>
    </Popup>
  );
};

export default ImprimirEtiquetaPopup;
